import { useState, useId, type ReactNode, type ComponentType } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';

interface CollapsibleSectionProps {
  /** Section title displayed in the header */
  title: string;
  /** Optional icon component displayed before the title */
  icon?: ComponentType<{ className?: string }>;
  /** Color variant for the section styling */
  variant?: 'default' | 'info' | 'success' | 'warning' | 'error';
  /** Whether the section is expanded by default */
  defaultExpanded?: boolean;
  /** Optional badge/count displayed in the header */
  badge?: string | number;
  /** Content to display when expanded */
  children: ReactNode;
  /** Additional class names for the container */
  className?: string;
}

const variantStyles = {
  default: {
    container: 'bg-dark-elevated-1 border-border-subtle',
    header: 'text-text-primary hover:bg-dark-elevated-2/60',
    icon: 'text-text-muted',
    badge: 'bg-dark-elevated-2 text-text-secondary',
    focus: 'focus-visible:ring-border-strong',
  },
  info: {
    container: 'bg-info/5 border-info/25',
    header: 'text-info hover:bg-info/10',
    icon: 'text-info',
    badge: 'bg-info/20 text-info',
    focus: 'focus-visible:ring-info/50',
  },
  success: {
    container: 'bg-success/5 border-success/25',
    header: 'text-success hover:bg-success/10',
    icon: 'text-success',
    badge: 'bg-success/20 text-success',
    focus: 'focus-visible:ring-success/50',
  },
  warning: {
    container: 'bg-warning/5 border-warning/25',
    header: 'text-warning hover:bg-warning/10',
    icon: 'text-warning',
    badge: 'bg-warning/20 text-warning',
    focus: 'focus-visible:ring-warning/50',
  },
  error: {
    container: 'bg-danger/5 border-danger/25',
    header: 'text-danger hover:bg-danger/10',
    icon: 'text-danger',
    badge: 'bg-danger/20 text-danger',
    focus: 'focus-visible:ring-danger/50',
  },
};

function CollapsibleSection({
  title,
  icon: Icon,
  variant = 'default',
  defaultExpanded = true,
  badge,
  children,
  className,
}: CollapsibleSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const contentId = useId();
  const styles = variantStyles[variant];

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <div
      className={cn(
        'rounded-lg border overflow-hidden',
        'transition-shadow duration-200',
        'hover:shadow-card',
        styles.container,
        className
      )}
    >
      {/* Header Button */}
      <button
        type="button"
        onClick={toggleExpanded}
        aria-expanded={isExpanded}
        aria-controls={contentId}
        className={cn(
          'w-full flex items-center justify-between gap-3 p-4',
          'text-left font-semibold',
          'transition-colors duration-150',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset',
          styles.header,
          styles.focus
        )}
      >
        <div className="flex items-center gap-3 min-w-0">
          {Icon && (
            <Icon
              className={cn(
                'h-5 w-5 flex-shrink-0',
                'transition-transform duration-200',
                styles.icon
              )}
            />
          )}
          <span className="truncate">{title}</span>
          {badge !== undefined && (
            <span
              className={cn(
                'inline-flex items-center justify-center',
                'px-2 py-0.5 text-xs font-medium rounded-full',
                'transition-opacity duration-150',
                styles.badge
              )}
            >
              {badge}
            </span>
          )}
        </div>

        {/* Chevron indicator */}
        <ChevronDownIcon
          className={cn(
            'h-5 w-5 flex-shrink-0',
            'transition-transform duration-200 ease-out',
            styles.icon,
            isExpanded && 'rotate-180'
          )}
        />
      </button>

      {/* Expandable Content */}
      <div
        id={contentId}
        role="region"
        aria-labelledby={contentId}
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out',
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="overflow-hidden">
          <div className="px-4 pb-4 pt-1">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CollapsibleSection;
